import { useEffect, useState } from 'react';
import { Form, Modal, message } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { HiSearch } from 'react-icons/hi';
import CustomButton from 'base/ui/components/Button';
import CustomSelect from 'base/ui/components/CustomSelect';
import CustomInput from 'base/ui/components/CustomInput';
import CustomProTable, { tablePagination } from 'base/ui/components/CustomTable';
import { DATE_HOUR_MINUTE_FORMAT_DEFAULT, PAGE_SIZE_DEFAULT } from 'utils/constants';
import { customFilterOption } from 'utils/searchHelper';
import {
  commonPresenter,
  commonStore,
  wasteHandoverPlanPresenter,
  wasteHandoverPlanStore,
} from 'stores/root_store';
import { WasteHandoverPlanModel } from 'services/Main/wasteHandoverPlan_services';
import { formatDateOnly } from './utils/dateHelpers';

interface SendMailModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

const SendMailModal = ({ open, onClose, onSuccess }: SendMailModalProps) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [vendorOptions, setVendorOptions] = useState<{ label: string; value: string }[]>([]);
  const [presetOptions, setPresetOptions] = useState<{ label: string; value: string }[]>([]);
  const [allData, setAllData] = useState<WasteHandoverPlanModel[]>([]); // Tất cả data để select all
  const [data, setData] = useState<WasteHandoverPlanModel[]>([]); // Data hiển thị theo trang
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
  const [loading, setLoading] = useState(false);
  const [sendLoading, setSendLoading] = useState(false);
  const [paging, setPaging] = useState({ pageIndex: 1, pageSize: PAGE_SIZE_DEFAULT });
  const [total, setTotal] = useState<number>(0);
  const [disableDateInputs, setDisableDateInputs] = useState(false);

  const loadVendors = async () => {
    if (vendorOptions.length) return;
    await commonPresenter.getDropdownVendor(commonStore, { type: 'NCC_CT' });
    if (commonStore.commonRespone?.isSuccess && commonStore.commonRespone?.data) {
      setVendorOptions(
        commonStore.commonRespone.data.map((item: any) => ({
          label: item.value || '',
          value: item.key || '',
        })),
      );
    }
  };

  const loadPresets = async () => {
    if (presetOptions.length) return;
    await commonPresenter.getListCommonDate(commonStore, 'CommonDate2');
    const opts = (commonStore.listCommonDate2 || []).map((item: any) => ({
      label: item.value,
      value: item.key,
    }));
    setPresetOptions(opts);
  };

  const fetchData = async (filters?: any, pagingOverride?: any) => {
    const currentPaging = pagingOverride || paging;
    setLoading(true);
    try {
      const f = filters ?? form.getFieldsValue();
      // Load tất cả data để có thể select all across pages
      const payloadAll = {
        paging: {
          pageIndex: 1,
          pageSize: 1000000000, // Lấy tất cả data
        },
        vendorNumber: Array.isArray(f.vendorNumber) ? f.vendorNumber.join(',') : f.vendorNumber,
        deliveryDateFrom: formatDateOnly(f.deliveryDateFrom),
        deliveryDateTo: formatDateOnly(f.deliveryDateTo),
        actived: true,
      };
      await wasteHandoverPlanPresenter.searchWasteHandoverPlans(wasteHandoverPlanStore, payloadAll);
      if (wasteHandoverPlanStore.searchWasteHandoverPlanResponse.isSuccess) {
        const apiData = wasteHandoverPlanStore.searchWasteHandoverPlanResponse.data || [];
        // Filter loại bỏ các item có isRegistered = true
        const filteredData = Array.isArray(apiData)
          ? apiData.filter(item => item.isRegistered !== true)
          : [];
        const mapped = filteredData.map((item, index) => ({
          ...item,
          stt: index + 1, // Tính lại STT từ 1
        }));

        // Lưu tất cả data để select all
        setAllData(mapped);
        setTotal(mapped.length);

        // Reset selectedKeys khi filter thay đổi - chỉ giữ lại các keys có trong allData mới
        const mappedKeys = mapped.map(item => item.wasteHandoverPlanId);
        setSelectedKeys(prevKeys => {
          // Chỉ giữ lại các keys có trong allData mới
          return prevKeys.filter(key => mappedKeys.includes(key as string));
        });

        // Phân trang hiển thị từ allData
        const startIndex = (currentPaging.pageIndex - 1) * currentPaging.pageSize;
        const endIndex = startIndex + currentPaging.pageSize;
        const paginatedData = mapped.slice(startIndex, endIndex);
        setData(paginatedData);
      } else {
        // Nếu API trả về không thành công, set data rỗng
        setAllData([]);
        setData([]);
        setTotal(0);
        message.error(wasteHandoverPlanStore.searchWasteHandoverPlanResponse.message);
      }
    } catch (error) {
      console.error(error);
      // Khi có lỗi, set data rỗng để tránh lỗi giao diện
      setAllData([]);
      setData([]);
      setTotal(0);
      message.error(t('waste-handover-plan.load-data-error'));
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setPaging({ pageIndex: 1, pageSize: paging.pageSize });
    fetchData(form.getFieldsValue(), { pageIndex: 1, pageSize: paging.pageSize });
  };

  const handleSend = async () => {
    if (!selectedKeys.length) {
      message.warning(t('message.required-select'));
      return;
    }
    setSendLoading(true);
    try {
      const f = form.getFieldsValue();
      const payload = {
        vendorNumber: Array.isArray(f.vendorNumber) ? f.vendorNumber.join(',') : f.vendorNumber,
        deliveryDateFrom: formatDateOnly(f.deliveryDateFrom),
        deliveryDateTo: formatDateOnly(f.deliveryDateTo),
        actived: true,
        wasteHandoverPlanIds: selectedKeys.map(key => String(key)),
      };
      const result = await wasteHandoverPlanPresenter.sendEmail(payload);
      if (result.isSuccess) {
        message.success(result.message || t('message.success'));
        onClose();
        // Gọi callback để load lại danh sách
        if (onSuccess) {
          onSuccess();
        }
      } else {
        message.error(result.message || t('message.error'));
      }
    } catch (error) {
      console.error(error);
      message.error(t('waste-handover-plan.send-email-error'));
    } finally {
      setSendLoading(false);
    }
  };

  useEffect(() => {
    if (open) {
      form.resetFields();
      setSelectedKeys([]);
      setAllData([]); // Reset allData khi mở modal
      setPaging({ pageIndex: 1, pageSize: PAGE_SIZE_DEFAULT });

      // Mặc định chọn "Today" và load dữ liệu
      const initDefaultFilters = async () => {
        // Load presets trước
        await loadPresets();

        // Set preset mặc định là "Today" và disable date inputs
        setDisableDateInputs(true);
        form.setFieldValue('deliveryPreset', 'Today');

        // Load ngày từ preset "Today"
        await commonPresenter.getDateByCommonDate(commonStore, 'Today');
        if (commonStore.commonDate?.fromDate) {
          // HTML5 date input cần format YYYY-MM-DD (string)
          const todayFrom = commonStore.commonDate.fromDate; // Đã là YYYY-MM-DD
          const todayTo = commonStore.commonDate.toDate; // Đã là YYYY-MM-DD
          form.setFieldsValue({
            deliveryDateFrom: todayFrom,
            deliveryDateTo: todayTo,
          });

          // Fetch data với filters mặc định (Today) - cần convert sang dayjs cho fetchData
          const todayFromDayjs = dayjs(todayFrom, 'YYYY-MM-DD');
          const todayToDayjs = dayjs(todayTo, 'YYYY-MM-DD');
          await fetchData({
            actived: true,
            vendorNumber: undefined,
            deliveryDateFrom: todayFromDayjs,
            deliveryDateTo: todayToDayjs,
          }, { pageIndex: 1, pageSize: PAGE_SIZE_DEFAULT });
        } else {
          // Nếu không load được ngày Today, fetch với filters rỗng
          setDisableDateInputs(false);
          await fetchData({ actived: true, vendorNumber: undefined, deliveryDateFrom: undefined, deliveryDateTo: undefined }, { pageIndex: 1, pageSize: PAGE_SIZE_DEFAULT });
        }
      };

      loadVendors();
      initDefaultFilters().catch((error) => {
        console.error('Error initializing default filters:', error);
        // Đảm bảo data luôn là array ngay cả khi có lỗi
        setData([]);
        setTotal(0);
      });
    }
  }, [open]);

  // Phân trang lại từ allData khi pageIndex hoặc pageSize thay đổi
  useEffect(() => {
    if (open && allData.length > 0) {
      const startIndex = (paging.pageIndex - 1) * paging.pageSize;
      const endIndex = startIndex + paging.pageSize;
      const paginatedData = allData.slice(startIndex, endIndex);
      setData(paginatedData);
    }
  }, [paging.pageIndex, paging.pageSize, allData, open]);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      width={900}
      footer={null}
      style={{ maxHeight: '80vh', top: 24, paddingTop: 0 }}
      bodyStyle={{
        padding: 0,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '80vh',
      }}
      closable={false}
      title={
        <div className="flex items-center justify-between px-3 -mt-2">
          <div className="font-bold text-sm uppercase">{t('btn.send-email')} kế hoạch bàn giao chất thải</div>
          <button
            onClick={onClose}
            aria-label="Close"
          >
            <CloseOutlined />
          </button>
        </div>
      }
    >
      <div className="px-3">
        <Form form={form} layout="vertical" onFinish={handleSearch}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-3">
            <Form.Item
              label={t('waste-handover-plan.delivery-date-preset')}
              name="deliveryPreset"
              style={{ marginBottom: 8 }}
            >
              <CustomSelect
                allowClear
                showSearch
                options={presetOptions}
                placeholder={t('place-holder.select')}
                filterOption={customFilterOption}
                onChange={async (value) => {
                  if (value && value !== 'Custom') {
                    // Có chọn preset (không phải Custom), load ngày từ preset và disable date inputs
                    setDisableDateInputs(true);
                    await commonPresenter.getDateByCommonDate(commonStore, value);
                    if (commonStore.commonDate?.fromDate) {
                      // HTML5 date input cần format YYYY-MM-DD (string)
                      const fromDate = commonStore.commonDate.fromDate; // Đã là YYYY-MM-DD
                      const toDate = commonStore.commonDate.toDate; // Đã là YYYY-MM-DD
                      form.setFieldsValue({
                        deliveryDateFrom: fromDate,
                        deliveryDateTo: toDate,
                      });
                    }
                  } else if (value === 'Custom') {
                    // Chọn Custom, enable date inputs
                    setDisableDateInputs(false);
                  } else {
                    // Xóa preset, cũng xóa từ ngày và đến ngày, enable date inputs
                    setDisableDateInputs(false);
                    form.setFieldsValue({
                      deliveryDateFrom: undefined,
                      deliveryDateTo: undefined,
                    });
                  }
                }}
              />
            </Form.Item>
            <Form.Item
              label={t('waste-handover-plan.vendor')}
              name="vendorNumber"
              style={{ marginBottom: 8 }}
            >
              <CustomSelect
                mode="multiple"
                allowClear
                showSearch
                options={vendorOptions}
                placeholder={t('place-holder.select')}
                filterOption={customFilterOption}
              />
            </Form.Item>
            <Form.Item
              label={t('waste-handover-plan.from-date')}
              name="deliveryDateFrom"
              style={{ marginBottom: 8 }}
            >
              <CustomInput type="date" disabled={disableDateInputs} />
            </Form.Item>
            <Form.Item
              label={t('waste-handover-plan.to-date')}
              name="deliveryDateTo"
              style={{ marginBottom: 8 }}
            >
              <CustomInput type="date" disabled={disableDateInputs} />
            </Form.Item>
          </div>
          <div className="relative flex justify-center items-center gap-x-3 mb-2">
            {selectedKeys.length > 0 && (
              <div className="absolute top-2 left-0 text-red-500 text-sm italic">
                Đã chọn: {selectedKeys.length} mục
              </div>
            )}
            <CustomButton
              onClick={() => {
                form.submit();
              }}
              icon={<HiSearch />}
            >
              {t('btn.search')}
            </CustomButton>
            <CustomButton
              type="primary"
              color="#22c55e"
              disabled={!selectedKeys.length}
              loading={sendLoading}
              onClick={handleSend}
            >
              {t('btn.send-email')}
            </CustomButton>
          </div>
        </Form>
      </div>
      <div style={{ flex: 1, padding: '0 12px 8px', overflow: 'hidden' }}>
        <CustomProTable<WasteHandoverPlanModel>
          columns={[
            {
              title: t('waste-handover-plan.stt'),
              dataIndex: 'stt',
              width: 60,
              align: 'center',
              fixed: 'left',
            },
            {
              title: t('waste-handover-plan.delivery-schedule-time'),
              dataIndex: 'deliveryDate',
              width: 180,
              align: 'center',
              valueType: 'dateTime',
              fieldProps: { format: DATE_HOUR_MINUTE_FORMAT_DEFAULT },
              render: (_, record) =>
                record.deliveryDate ? dayjs(record.deliveryDate).format(DATE_HOUR_MINUTE_FORMAT_DEFAULT) : '-',
            },
            {
              title: t('masterdata.waste-grouping-contract.title'),
              dataIndex: 'wasteGroupingContractName',
              width: 180,
              ellipsis: true,
            },
            {
              title: t('waste-handover-plan.waste-name'),
              dataIndex: 'materialName',
              width: 260,
              ellipsis: true,
              render: (_, record) =>
                record.productCode
                  ? `${record.productCode} | ${record.materialName || ''}`
                  : record.materialName || '-',
            },
            {
              title: t('waste-handover-plan.vendor'),
              dataIndex: 'vendorName',
              width: 240,
              ellipsis: true,
              render: (_, record) =>
                record.vendorNumber
                  ? `${record.vendorName || ''}`
                  : record.vendorName || '-',
            },
            {
              title: t('waste-handover-plan.storage-location'),
              dataIndex: 'storageLocationName',
              width: 160,
              render: (_, record) => record.storageLocationName || record.storageLocation || '-',
            },
          ]}
          rowKey="wasteHandoverPlanId"
          size="small"
          bordered
          loading={loading}
          search={false}
          dataSource={data || []}
          rowSelection={{
            selectedRowKeys: selectedKeys || [],
            onChange: (keys, selectedRows, info) => {
              // Chỉ xử lý select/deselect từng item, không xử lý select all ở đây
              if (info.type !== 'all') {
                // Select/deselect từng item
                // Cần merge với các keys đã chọn từ các trang khác
                const currentPageKeys = data.map(item => item.wasteHandoverPlanId as React.Key);
                const otherPageKeys = selectedKeys.filter(key => !currentPageKeys.includes(key));
                const newKeys = [...otherPageKeys, ...(keys || [])];
                setSelectedKeys(newKeys);
              }
              // Nếu info.type === 'all', để onSelectAll xử lý
            },
            onSelectAll: (selected, selectedRows, changeRows) => {
              // Xử lý select all/deselect all từ onSelectAll callback
              if (selected) {
                // Select all: chọn tất cả items trong allData
                const allKeys = allData.map(item => item.wasteHandoverPlanId as React.Key);
                setSelectedKeys(allKeys);
              } else {
                // Deselect all: bỏ chọn tất cả items trong allData
                setSelectedKeys([]);
              }
            },
            preserveSelectedRowKeys: true, // Preserve để giữ selection khi chuyển trang
            fixed: true, // Fix checkbox khi scroll ngang
            columnWidth: 52, // Đảm bảo đủ không gian cho checkbox
            renderCell: (_value, _record, _index, originNode) => (
              <div className="min-w-[40px] flex justify-center">{originNode}</div>
            ),
          }}
          tableAlertRender={false}
          tableAlertOptionRender={false}
          scroll={{ x: 'max-content', y: 'calc(70vh - 240px)' }}
          pagination={{
            ...tablePagination(
              { paging, filters: {} } as any,
              (v: any) =>
                setPaging({
                  pageIndex: v.paging.pageIndex,
                  pageSize: v.paging.pageSize,
                }),
            ),
            total,
          }}
          options={false}
        />
      </div>
    </Modal>
  );
};

export default SendMailModal;

